import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getInsightContentTypeDisplayText } from 'components/insightContent/utils/utils'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
// @ts-ignore
import { Tooltip, Dialog } from '@netbasequid/core'
import AccordionSectionType from 'components/insightContent/enum/AccordionSectionType'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { CountTypeStrings } from 'enums/CountType'
import { InsightContentSettingType } from 'components/insightContent/types'
import AccordionGroup from 'components/insightContent/visualization/AccordionGroup'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { sendTypedMessage } from 'utils/iframeUtil'
import { toExternal } from 'vo/util'
import {
  TopInfoBox,
  TopInfoRight,
  StyledTermContainer,
  StyledTermContent
} from 'components/insightContent/visualization/InfoBox/index.css'
import GoToAnalysisButton from 'components/insightContent/visualization/GoToAnalysisButton'
import InsightDialog from 'components/insightContent/visualization/InsightDialog'

const DIALOG_CONTENT_HEIGHT = '460px'

const InsightModal = ({
  open,
  title,
  countType,
  selectedInsights,
  onClose,
  detailSetting,
  timeUnits,
  forceNotScale,
  widgetSettingWithSelection
}: {
  open: boolean
  title: string | undefined
  countType: CountTypeStrings
  selectedInsights: InsightData[] | undefined
  onClose: () => void
  detailSetting: InsightContentSettingType | undefined
  timeUnits?: MetricTimeUnitStrings[]
  forceNotScale?: boolean
  widgetSettingWithSelection?: any
}) => {
  const theme = useDisplayTheme() as Theme
  const [disableAnalyzeBtn, setDisableAnalyzeBtn] = useState(false)
  const [displayTooltip, setDisplayTooltip] = useState(false)
  const { t } = useTranslation()

  const checkTermContent = useCallback((node: HTMLDivElement) => {
    if (!node) return

    setDisplayTooltip(node.scrollHeight! > node.clientHeight!)
  }, [])

  useEffect(() => {
    if (selectedInsights?.length) {
      const isInsightValid = (insight: InsightData) =>
        insight.mentions === undefined || insight.mentions > 0

      if (selectedInsights.some(isInsightValid)) {
        setDisableAnalyzeBtn(false)
      } else {
        setDisableAnalyzeBtn(true)
      }
    }
  }, [selectedInsights])

  function goToAnalysis() {
    sendTypedMessage('goToAnalysis', toExternal(widgetSettingWithSelection))
  }

  return (
    <InsightDialog open={open} onClose={onClose}>
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <TopInfoBox mode={theme}>
        {selectedInsights?.map((item: InsightData, index: number) => (
          <StyledTermContainer
            ref={checkTermContent}
            // eslint-disable-next-line react/no-array-index-key
            key={`${item.authorID}-${item.authorName || item.name}-${index}`}>
            <Tooltip mode={theme} enabled={displayTooltip} content={item.authorName || item.name}>
              <StyledTermContent
                // eslint-disable-next-line react/no-array-index-key
                key={`${item.authorID}-${item.authorName || item.name}-${index}`}
                mode={theme}>
                {item.authorName || item.name}
              </StyledTermContent>
            </Tooltip>
          </StyledTermContainer>
        ))}
        <TopInfoRight>
          <Tooltip
            mode={theme}
            enabled={disableAnalyzeBtn}
            content={t('authorsInsightModalBtnTooltip')}>
            <div>
              <GoToAnalysisButton onClick={() => goToAnalysis()} disabled={disableAnalyzeBtn} />
            </div>
          </Tooltip>
        </TopInfoRight>
      </TopInfoBox>
      <Dialog.DialogContent height={DIALOG_CONTENT_HEIGHT}>
        <AccordionGroup
          timeUnits={timeUnits}
          forceNotScale={forceNotScale}
          countType={countType}
          insightContentsConfig={[
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SUMMARY),
              accordionContents: [
                {
                  insightContentType: InsightContentType.TOTAL_MEASURE,
                  detailSetting
                },
                {
                  insightContentType: InsightContentType.NAME_MEASURE_BY,
                  detailSetting
                }
              ]
            },
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SOUND_BITE),
              accordionContents: [
                {
                  insightContentType: InsightContentType.HEADER_TWEET_SOUND_BITE,
                  detailSetting
                }
              ]
            }
          ]}
        />
      </Dialog.DialogContent>
      <Dialog.DialogActions />
    </InsightDialog>
  )
}

export default InsightModal
